import Decimal from "decimal.js-light";
import { nanoid } from "nanoid";
import qs from "query-string";
import snakecaseKeys from "snakecase-keys";

import * as companySessionSelectors from "@/components/new/CompanySession/company-session.selectors";
import {
  getLinealFeetQuantityInFeet,
  getLinealMetresQuantityInMetres,
  getSquareFeetQuantityInSquareFeet,
  getSquareMetresQuantityInSquareMetres,
} from "@/utils/measurement/measurement-for-price-computation";
import { parseApiError } from "@/utils/parseApiError";

import { getCollaborationNotesFile, saveMessageWithFiles } from "../../api/purchase";
import * as priceLevelsApi from "../../apiv2/priceLevels";
import * as productsApi from "../../apiv2/products";
import {
  bulkDeletePurchaseOrders,
  bulkRestorePurchaseOrders,
  createPurchaseOrder,
  deletePurchaseOrder,
  emailPurchaseOrder,
  getExportPurchaseOrders,
  getExternalPurchaseOrder,
  getLinkedOrdersFromPurchaseOrder,
  getPurchaseOrder,
  // ATTACHMENTS
  getPurchaseOrderAttachments,
  patchUpdateProductsIndex,
  postChangeSupplier,
  postDuplicateOrder,
  postPrintPurchaseOrderPdf,
  searchPurchaseOrders,
  updatedCompletedItems,
  updatePurchaseOrder,
} from "../../apiv2/purchaseOrders";
import { ROOT_URL, resolveFileUrl } from "../../env";
import {
  INTENT,
  PRICING_STRATEGY_LINEAL_FEET,
  PRICING_STRATEGY_LINEAL_METRES,
  PRICING_STRATEGY_SQUARE_FEET,
  PRICING_STRATEGY_SQUARE_METRES,
  PRODUCT_TYPE_ADDITIONAL_PRODUCT,
  STATUS,
} from "../../utils/constants";
import * as alertActions from "../alerts/alerts.actions";
import { invalidateSearchCacheIfGoSearch } from "../globalSearch/globalSearch.actions";
import dispatchApi from "../utils/dispatchApi";
import { handleError } from "../utils/error";

import {
  BATCH_EDIT_ACTIVE_PURCHASE_ORDER,
  BULK_DELETE_PURCHASE_ORDERS,
  CREATE_PURCHASE_ORDER,
  DELETE_PURCHASE_ORDER,
  DRAFT_PURCHASE_ORDER,
  EDIT_ACTIVE_PURCHASE_ORDER,
  EDIT_ACTIVE_PURCHASE_ORDER_SILENTLY,
  EDIT_DRAWING_SVGS,
  EDIT_PURCHASE_ORDER_FILTERS,
  EMAIL_PURCHASE_ORDER_STATUS,
  EXPORT_PURCHASE_ORDERS_STATUS,
  GET_EXTERNAL_PURCHASE_ORDER,
  GET_LINKED_ORDERS_FROM_PURCHASE_ORDERS,
  GET_PURCHASE_ORDER,
  LOAD_EMPTY_PURCHASE_ORDER,
  PRINT_PURCHASE_ORDER_PDF_STATUS,
  REMOVE_SELECTED_ATTACHMENT,
  RESET_ACTIVE_PURCHASE_ORDER,
  RESET_PURCHASE_ORDERS,
  SEARCH_PURCHASE_ORDERS,
  // Pagination
  SET_CURRENT_PAGE,
  SET_DUPLICATE_ORDER_SUPPLIER_UID,
  SET_DUPLICATE_ORDER_UID,
  SET_IS_ADD_PRODUCT_MODAL_TYPE,
  SET_IS_CHANGING_SUPPLIER,
  SET_IS_CHANGING_SUPPLIER_MODAL_OPEN,
  SET_IS_CONVERTING_BILL_OR_PO,
  SET_IS_CONVERTING_BILL_OR_PO_CONFIRMATION_ALERT_OPEN,
  SET_IS_DUPLICATE_CONFIRMATION_ALERT_OPEN,
  SET_IS_DUPLICATING,
  // ATTACHMENTS
  SET_IS_FETCHING,
  SET_NEXT_PAGE_OFFSET,
  SET_ORDER_ATTACHMENTS,
  SET_PAGE_LIMIT,
  SET_PAGE_OFFSET,
  SET_PREVIOUS_PAGE_OFFSET,
  SET_SELECTED_ATTACHMENTS,
  SET_SORT,
  SET_TOTAL_COUNT,
  UNDO_PURCHASE_ORDER_ARCHIVE,
  UNDO_PURCHASE_ORDER_BULK_ARCHIVE,
  UPDATE_PRODUCTS_INDEX,
  UPDATE_PURCHASE_ORDER,
  UPDATE_PURCHASE_ORDER_SILENTLY,
  UPDATE_RECEIVED_ITEMS,
  UPDATE_SELECTED_ATTACHMENTS,
} from "./purchaseOrders.actionTypes";
import * as selectors from "./purchaseOrders.selectors";

Decimal.config({ rounding: Decimal.ROUND_HALF_UP });

export const searchPurchaseOrdersAction =
  ({ search = "", startDate = "", endDate = "", statuses = [], uid = "" } = {}) =>
  async (dispatch, getState) => {
    const state = getState();

    try {
      const limit = selectors.getPagination(state).limit;
      const offset = selectors.getPagination(state).offset;
      const sort = selectors.getSort(state);
      const params = {
        search: search || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        statuses,
        limit,
        offset,
        sort_by: sort !== "" ? sort : undefined,
        uid: uid || undefined,
      };

      const response = await dispatchApi(
        dispatch,
        SEARCH_PURCHASE_ORDERS,
        () =>
          searchPurchaseOrders({
            params,
          }),
        INTENT.PERSISTENT
      );

      dispatch(setTotalCount(response.count));
      dispatch(setCurrentPage(offset / limit));
      if (response.next) {
        const parsedNextPage = qs.parse(response.next);
        dispatch(setNextPageOffset(parsedNextPage?.offset ?? 0));
      }

      if (response.previous) {
        const parsedPreviousPage = qs.parse(response.previous);
        dispatch(setPreviousPageOffset(parsedPreviousPage?.offset ?? 0));
      }
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const getPurchaseOrderAction = (purchaseOrderUid) => {
  return (dispatch) => {
    dispatchApi(
      dispatch,
      GET_PURCHASE_ORDER,
      () => getPurchaseOrder(purchaseOrderUid),
      INTENT.PERSISTENT
    );
  };
};

export const loadEmptyPurchaseOrderAction = (supplierUid, company, supplier, user) => ({
  type: LOAD_EMPTY_PURCHASE_ORDER,
  payload: { supplierUid, company, supplier, user },
});

export const batchEditActivePurchaseOrderAction = (changes) => ({
  type: BATCH_EDIT_ACTIVE_PURCHASE_ORDER,
  payload: { changes },
});

export const editActivePurchaseOrderAction = (path, value) => ({
  type: EDIT_ACTIVE_PURCHASE_ORDER,
  payload: { path, value },
});

export const editActivePurchaseOrderSilentlyAction = (path, value) => ({
  type: EDIT_ACTIVE_PURCHASE_ORDER_SILENTLY,
  payload: { path, value },
});

export const resetActivePurchaseOrderAction = () => ({
  type: RESET_ACTIVE_PURCHASE_ORDER,
  payload: {},
});

export const updatePurchaseOrderAction = (purchaseOrderUid, data) => {
  return async (dispatch) => {
    const result = await dispatchApi(
      dispatch,
      UPDATE_PURCHASE_ORDER,
      () => updatePurchaseOrder(purchaseOrderUid, data),
      INTENT.PERSISTENT
    );
    dispatch(invalidateSearchCacheIfGoSearch("purchase_orders"));
    return result;
  };
};

export const updatePurchaseOrderSilently = (purchaseOrderUid) => {
  return async (dispatch, getState) => {
    const state = getState();
    const activePurchaseOrder = selectors.activePurchaseOrderSelector(state);
    const payload = {
      ...activePurchaseOrder,
      editStatus: activePurchaseOrder.editStatus.id,
      requiredDate: activePurchaseOrder.requiredDate ? activePurchaseOrder.requiredDate : null,
      deletedItems: activePurchaseOrder.uid ? activePurchaseOrder.deletedItems : [],
      deletedFlashingItems: activePurchaseOrder.uid ? activePurchaseOrder.deletedFlashingItems : [],
    };
    return await dispatchApi(
      dispatch,
      UPDATE_PURCHASE_ORDER_SILENTLY,
      () => updatePurchaseOrder(purchaseOrderUid, payload),
      INTENT.PERSISTENT
    );
  };
};

export const updateProductsIndex = (purchaseOrderUid, data) => {
  return async (dispatch) => {
    return await dispatchApi(
      dispatch,
      UPDATE_PRODUCTS_INDEX,
      () => patchUpdateProductsIndex(purchaseOrderUid, data),
      INTENT.PERSISTENT
    );
  };
};
const getQuantity = ({ item, state }) => {
  const minLengthForLinealFeet = companySessionSelectors.getMinLengthLinealFeet(state);
  const minLengthForLinealMeters = companySessionSelectors.getMinLengthLinealMeters(state);

  if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES) {
    const totalLength = getLinealMetresQuantityInMetres({
      items: (item.lengths ?? []).map((l) => ({ length: l.lengthInMm, amount: l.quantity })),
      minLengthInMm: minLengthForLinealMeters,
    });
    return parseFloat(totalLength?.forPrice ?? 0);
  }

  if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES) {
    const totalSqm = getSquareMetresQuantityInSquareMetres({
      items: (item.dimensions ?? []).map((s) => ({ ...s, amount: s.quantity })),
    });
    return parseFloat(totalSqm.total ?? 0);
  }

  if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET) {
    const totalLength = getLinealFeetQuantityInFeet({
      items: (item.lengths ?? []).map((l) => ({ length: l.lengthInMm, amount: l.quantity })),
      minLengthInMm: minLengthForLinealFeet,
    });

    return parseFloat(totalLength?.forPrice ?? 0);
  }

  if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET) {
    const totalSqft = getSquareFeetQuantityInSquareFeet({
      items: (item.dimensions ?? []).map((d) => ({
        totalAreaInSquareFeet: d.displayDimensions?.totalAreaInSquareFeet ?? 0,
      })),
    });
    return parseFloat(totalSqft.total ?? 0);
  }

  return parseFloat(item.quantity) || 0;
};

export const recalculateProductLineItemPrices =
  ({ rowIndex, key }) =>
  (dispatch, getState) => {
    const state = getState();
    const activePurchaseOrder = selectors.activePurchaseOrderSelector(state);
    const item = activePurchaseOrder.items.find((_, index) => index === rowIndex);

    if (!item) {
      return;
    }

    const isPercentageDiscount = item.discountIsPercentage !== false;
    const discountValue = isPercentageDiscount
      ? item.discountRateInPercent
      : item.discountInDollars;
    const discount = parseFloat(discountValue) > 0 ? discountValue : 0;

    const quantity = getQuantity({ item, state });

    if (key === "total") {
      const isZeroQuantity = Decimal(quantity).isZero();
      let itemTotalWithDiscount;
      if (isZeroQuantity) {
        itemTotalWithDiscount = Decimal(0);
      } else if (isPercentageDiscount) {
        const discountRate = Decimal(100).minus(discount);
        itemTotalWithDiscount = discountRate.isZero()
          ? Decimal(0)
          : Decimal(item.total).dividedBy(discountRate).times(100);
      } else {
        itemTotalWithDiscount = Decimal(item.total).plus(discount);
      }
      const newUnitPrice = isZeroQuantity
        ? Decimal(0)
        : itemTotalWithDiscount.dividedBy(quantity).toDecimalPlaces(4);

      let recomputedTotal;
      if (isZeroQuantity) {
        recomputedTotal = Decimal(0);
      } else if (isPercentageDiscount) {
        const discountMultiplier = Decimal(100).minus(discount).dividedBy(100);
        recomputedTotal = newUnitPrice.times(quantity).times(discountMultiplier).toDecimalPlaces(2);
      } else {
        recomputedTotal = newUnitPrice.times(quantity).minus(Decimal(discount)).toDecimalPlaces(2);
      }

      dispatch(
        editActivePurchaseOrderAction(
          ["items", rowIndex, "productRowPrice"],
          parseFloat(newUnitPrice.toFixed(4))
        )
      );

      dispatch(
        editActivePurchaseOrderAction(["items", rowIndex, "priceOverrides"], {
          total: parseFloat(recomputedTotal.toFixed(2)),
          itemPrice: parseFloat(newUnitPrice.toFixed(4)),
        })
      );

      dispatch(
        editActivePurchaseOrderAction(
          ["items", rowIndex, "total"],
          parseFloat(recomputedTotal.toFixed(2))
        )
      );
    } else {
      const itemUnitPrice = item.priceOverrides?.itemPrice
        ? Decimal(item.priceOverrides.itemPrice).toDecimalPlaces(4)
        : Decimal(item.productRowPrice).toDecimalPlaces(4);

      let newTotalWithDiscount;
      if (isPercentageDiscount) {
        const discountMultiplier = Decimal(1).minus(Decimal(discount).dividedBy(100));
        newTotalWithDiscount = itemUnitPrice
          .times(quantity)
          .times(discountMultiplier)
          .toDecimalPlaces(2);
      } else {
        newTotalWithDiscount = itemUnitPrice
          .times(quantity)
          .minus(Decimal(discount))
          .toDecimalPlaces(2);
      }

      // Store value
      if (item.priceOverrides?.itemPrice) {
        dispatch(
          editActivePurchaseOrderAction(
            ["items", rowIndex, "priceOverrides", "itemPrice"],
            parseFloat(itemUnitPrice)
          )
        );
      }

      if (item.priceOverrides?.total) {
        dispatch(
          editActivePurchaseOrderAction(
            ["items", rowIndex, "priceOverrides", "total"],
            parseFloat(newTotalWithDiscount)
          )
        );
      }
      dispatch(
        editActivePurchaseOrderAction(
          ["items", rowIndex, "total"],
          parseFloat(newTotalWithDiscount)
        )
      );
    }

    dispatch(recalculateOrderSummaryPrice());
  };

export const recalculateOrderSummaryPrice = () => (dispatch, getState) => {
  const state = getState();
  const activePurchaseOrder = selectors.activePurchaseOrderSelector(state);
  const company = companySessionSelectors.getCurrentCompany(state);

  const productsPriceTotal = activePurchaseOrder.items
    .filter((item) => !activePurchaseOrder.deletedItems.includes(item.id))
    .reduce((acc, val) => {
      const decimalAcc = Decimal(acc);
      return Decimal(val?.priceOverrides?.total ?? val.total).plus(decimalAcc);
    }, Decimal(0));

  // Per-line GST rounding matches Xero/QBO: round each line's GST to 2dp,
  // then sum. See docs/claude/gst-calculation.md.
  const productsGstTotal = activePurchaseOrder.items
    .filter((item) => !activePurchaseOrder.deletedItems.includes(item.id) && !item.isTaxFree)
    .reduce((acc, val) => {
      const decimalAcc = Decimal(acc);
      const gstRate = Decimal(company.taxRate);
      const itemTotal = Decimal(val?.priceOverrides?.total ?? val.total);
      const itemGst = itemTotal.times(gstRate).toDecimalPlaces(2);
      return decimalAcc.plus(itemGst);
    }, Decimal(0));

  const flashingPriceTotal = activePurchaseOrder.flashingItems
    .filter((item) => !activePurchaseOrder.deletedFlashingItems.includes(item.id))
    .reduce((acc, val) => {
      const decimalAcc = Decimal(acc);
      return Decimal(val.totalValue).plus(decimalAcc);
    }, Decimal(0));

  const flashingGstTotal = activePurchaseOrder.flashingItems
    .filter((item) => !activePurchaseOrder.deletedFlashingItems.includes(item.id))
    .reduce((acc, val) => {
      const decimalAcc = Decimal(acc);
      const gstRate = Decimal(company.taxRate);
      const itemGst = Decimal(val.totalValue).times(gstRate).toDecimalPlaces(2);
      return decimalAcc.plus(itemGst);
    }, Decimal(0));

  let sumSubTotal = productsPriceTotal.plus(flashingPriceTotal);
  let gstTotal = productsGstTotal.plus(flashingGstTotal);

  // Apply discounts sequentially in entry order so dollar-then-percent and
  // percent-then-dollar produce different results.
  const preDiscountSubTotal = sumSubTotal;
  activePurchaseOrder.discountRates.forEach((discount) => {
    if (discount.discountIsPercentage !== false) {
      const value = parseFloat(discount.discountRateInPercent);
      if (!Number.isNaN(value) && value > 0) {
        sumSubTotal = sumSubTotal.times(Decimal(1).minus(Decimal(value).dividedBy(100)));
      }
    } else {
      const value = parseFloat(discount.discountInDollars);
      if (!Number.isNaN(value) && value > 0) {
        sumSubTotal = sumSubTotal.minus(value);
      }
    }
  });

  if (sumSubTotal.lessThan(preDiscountSubTotal) && preDiscountSubTotal.greaterThan(0)) {
    sumSubTotal = sumSubTotal.toDecimalPlaces(2);
    const discountRatio = sumSubTotal.dividedBy(preDiscountSubTotal);
    gstTotal = gstTotal.times(discountRatio).toDecimalPlaces(2);
  }

  const deliveryFee = Decimal(activePurchaseOrder.deliveryFee).toDecimalPlaces(2);
  const deliveryFeeGst = deliveryFee.times(Decimal(company.taxRate)).toDecimalPlaces(2);

  sumSubTotal = sumSubTotal.plus(deliveryFee);
  const totalGst = gstTotal.plus(deliveryFeeGst);

  // Round once at the display boundary, then compute the displayed total as
  // displayed subtotal + displayed GST so the user-facing total always
  // matches what they see for subtotal and GST.
  const displayedSubTotal = sumSubTotal.toDecimalPlaces(2);
  const displayedGst = totalGst.toDecimalPlaces(2);
  const displayedTotal = displayedSubTotal.plus(displayedGst);

  const priceChanges = [
    { path: ["itemsAndDelivery"], value: displayedSubTotal.toFixed(2) },
    { path: ["gstAmount"], value: displayedGst.toFixed(2) },
    { path: ["total"], value: displayedTotal.toFixed(2) },
  ];

  dispatch(batchEditActivePurchaseOrderAction(priceChanges));
};

export const updatedCompletedItemsAction = (purchaseOrderUid) => async (dispatch, getState) => {
  const state = getState();
  const activePurchaseOrder = selectors.activePurchaseOrderSelector(state);
  const products = activePurchaseOrder.items.map((item) => item);
  const flashings = activePurchaseOrder.flashingItems.map((item) => item);

  const data = {
    received: products.filter((item) => item.isReceived).map((item) => item.id),
    not_received: products.filter((item) => !item.isReceived).map((item) => item.id),
    flashing_received: flashings
      .filter((flashing) => flashing.isReceived)
      .map((flashing) => flashing.id),
    flashing_not_received: flashings
      .filter((flashing) => !flashing.isReceived)
      .map((flashing) => flashing.id),
  };

  const response = await dispatchApi(
    dispatch,
    UPDATE_RECEIVED_ITEMS,
    () => updatedCompletedItems(purchaseOrderUid, data),
    INTENT.TRANSIENT
  );

  if (Array.isArray(response?.data)) {
    const inventoryChanges = response.data.map((d) => {
      const itemIndex = activePurchaseOrder.items.findIndex((i) => i.id === d.id);
      return {
        path: ["items", itemIndex, "inventory"],
        value: d.inventory,
      };
    });
    dispatch(batchEditActivePurchaseOrderAction(inventoryChanges));
    dispatch(editActivePurchaseOrderAction(["dirty"], false));
  }

  return response;
};

export const createPurchaseOrderAction = (data) => {
  return async (dispatch) => {
    const result = await dispatchApi(
      dispatch,
      CREATE_PURCHASE_ORDER,
      () => createPurchaseOrder(data),
      INTENT.PERSISTENT
    );
    dispatch(invalidateSearchCacheIfGoSearch("purchase_orders"));
    return result;
  };
};

export const draftPurchaseOrderAction = (purchaseOrderUid) => {
  return async (dispatch) => {
    return await dispatchApi(
      dispatch,
      DRAFT_PURCHASE_ORDER,
      () => updatePurchaseOrder(purchaseOrderUid, { editStatus: 1 }),
      INTENT.TRANSIENT
    );
  };
};

export const deletePurchaseOrderAction = (purchaseOrderUid) => {
  return async (dispatch) => {
    await dispatchApi(
      dispatch,
      DELETE_PURCHASE_ORDER,
      () => deletePurchaseOrder(purchaseOrderUid),
      INTENT.PERSISTENT
    );
    dispatch(invalidateSearchCacheIfGoSearch("purchase_orders"));
  };
};

export const bulkDeletePurchaseOrdersAction = (purchaseOrderUids) => {
  return async (dispatch) => {
    await dispatchApi(
      dispatch,
      BULK_DELETE_PURCHASE_ORDERS,
      () => bulkDeletePurchaseOrders(purchaseOrderUids),
      INTENT.PERSISTENT
    );
    dispatch(invalidateSearchCacheIfGoSearch("purchase_orders"));
  };
};

export const editPurchaseOrderFiltersAction = (path, value) => ({
  type: EDIT_PURCHASE_ORDER_FILTERS,
  payload: { path, value },
});

export const undoPurchaseOrderArchiveAction = (purchaseOrderUid) => {
  return async (dispatch) => {
    await dispatchApi(
      dispatch,
      UNDO_PURCHASE_ORDER_ARCHIVE,
      () => updatePurchaseOrder(purchaseOrderUid, { isDeleted: false }),
      INTENT.PERSISTENT
    );
    dispatch(invalidateSearchCacheIfGoSearch("purchase_orders"));
  };
};
export const undoPurchaseOrderBulkArchiveAction = (purchaseOrderUids) => {
  return async (dispatch) => {
    await dispatchApi(
      dispatch,
      UNDO_PURCHASE_ORDER_BULK_ARCHIVE,
      () => bulkRestorePurchaseOrders(purchaseOrderUids),
      INTENT.PERSISTENT
    );
    dispatch(invalidateSearchCacheIfGoSearch("purchase_orders"));
  };
};

export const getExternalPurchaseOrderAction = (externalToken) => {
  return (dispatch) => {
    dispatchApi(
      dispatch,
      GET_EXTERNAL_PURCHASE_ORDER,
      () => getExternalPurchaseOrder(externalToken),
      INTENT.PERSISTENT
    );
  };
};

export const getLinkedPurchaseOrdersAction = ({ customerUid, orderUid }) => {
  return (dispatch) => {
    dispatchApi(
      dispatch,
      GET_LINKED_ORDERS_FROM_PURCHASE_ORDERS,
      () => getLinkedOrdersFromPurchaseOrder({ customerUid, orderUid }),
      INTENT.PERSISTENT
    );
  };
};

export const resetPurchaseOrdersAction = () => ({
  type: RESET_PURCHASE_ORDERS,
});

// ATTACHMENTS
export const setIsFetching = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING,
    payload: status,
  });
};

export const setSelectedAttachments = (data) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_ATTACHMENTS,
    payload: data,
  });
};

export const setOrderAttachments = (data) => (dispatch) => {
  dispatch({
    type: SET_ORDER_ATTACHMENTS,
    payload: data,
  });
};

export const removeSelectedAttachment = (data) => (dispatch) => {
  dispatch({
    type: REMOVE_SELECTED_ATTACHMENT,
    payload: data,
  });
};

export const updateSelectedAttachments = (data) => (dispatch) => {
  dispatch({
    type: UPDATE_SELECTED_ATTACHMENTS,
    payload: data,
  });
};

export const setPrintPurchaseOrderPdfStatus = (data) => (dispatch) => {
  dispatch({
    type: PRINT_PURCHASE_ORDER_PDF_STATUS,
    payload: data,
  });
};

export const setEmailPurchaseOrderStatus = (data) => (dispatch) => {
  dispatch({
    type: EMAIL_PURCHASE_ORDER_STATUS,
    payload: data,
  });
};

export const setExportPurchaseOrdersStatus = (data) => (dispatch) => {
  dispatch({
    type: EXPORT_PURCHASE_ORDERS_STATUS,
    payload: data,
  });
};

export const addAttachments =
  ({ purchaseOrderUid, file }) =>
  async (dispatch) => {
    dispatch(setIsFetching(true));
    const data = new FormData();
    const token = JSON.parse(localStorage.getItem("auth"));

    data.set(
      "json",
      JSON.stringify({
        purchase_order_uid: purchaseOrderUid,
        message: "",
      })
    );

    data.append("files", file);

    try {
      const res = await saveMessageWithFiles(token, data);
      // side effects
      const response = res.data;
      const attachmentId = response?.order_chat_message_file[0].id ?? "";
      const attachmentType = response?.order_chat_message_file[0].type ?? "";
      const attachmentFullUrl = response?.order_chat_message_file[0].full_url ?? "";

      dispatch(
        setSelectedAttachments({
          id: attachmentId,
          type: attachmentType,
          fullUrl: attachmentFullUrl,
        })
      );

      const params = {
        purchase_order_uid: purchaseOrderUid,
      };
      const attachmentResponse = await getPurchaseOrderAttachments({ params })();
      dispatch(setOrderAttachments(attachmentResponse));
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setIsFetching(false));
    }
  };

export const viewFile =
  ({ supplierUid, purchaseOrderUid, fileId }) =>
  async (dispatch) => {
    const token = JSON.parse(localStorage.getItem("auth"));
    dispatch(setIsFetching(true));

    await getCollaborationNotesFile(supplierUid, purchaseOrderUid, fileId, token).then(
      (response) => {
        const fileUrl = window.URL.createObjectURL(
          new File([response.data], fileId, { type: response.data.type })
        );
        window.open(fileUrl);
      }
    );

    dispatch(setIsFetching(false));
  };

export const fetchPurchaseOrderAttachments =
  ({ purchaseOrderUid }) =>
  async (dispatch) => {
    dispatch(setIsFetching(true));
    try {
      const params = {
        purchase_order_uid: purchaseOrderUid,
      };
      const response = await getPurchaseOrderAttachments({ params })();
      dispatch(setOrderAttachments(response));
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setIsFetching(false));
    }
  };

export const generatePdf =
  ({ purchaseOrderUid, drawingSVGs, withPrices, imageAttachmentUids, pdfAttachmentUids }) =>
  async (dispatch) => {
    const data = new FormData();

    Object.keys(drawingSVGs).forEach((key) => {
      const drawing = drawingSVGs[key];
      const { uid, svgXml } = drawing;
      const svgBlob = new Blob([svgXml], { type: "image/svg+xml" });
      const file = new File([svgBlob], uid, {
        type: "image/svg+xml",
        lastModified: Date.now(),
      });
      data.set(uid, file);
    });

    data.set(
      "json",
      JSON.stringify({
        with_prices: withPrices,
        attachment_images: imageAttachmentUids,
        attachment_pdfs: pdfAttachmentUids,
      })
    );

    try {
      dispatch(setPrintPurchaseOrderPdfStatus(STATUS.PENDING));
      const { data: pdfUrl } = await postPrintPurchaseOrderPdf({ purchaseOrderUid, data })();
      return `${ROOT_URL}/${pdfUrl}`;
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setPrintPurchaseOrderPdfStatus(STATUS.FULFILLED));
    }
  };

export const generateEmailPdf =
  ({ purchaseOrderUid, emails, drawingSVGs, withPrices, attachmentUids, customEmailMessage }) =>
  async (dispatch) => {
    const data = new FormData();

    Object.keys(drawingSVGs).forEach((key) => {
      const drawing = drawingSVGs[key];
      const { uid, svgXml } = drawing;
      const svgBlob = new Blob([svgXml], { type: "image/svg+xml" });
      const file = new File([svgBlob], uid, {
        type: "image/svg+xml",
        lastModified: Date.now(),
      });
      data.set(uid, file);
    });

    data.set(
      "json",
      JSON.stringify({
        with_prices: withPrices,
        emails,
        attachments: attachmentUids,
        custom_email_message: customEmailMessage,
      })
    );

    try {
      dispatch(setEmailPurchaseOrderStatus(STATUS.PENDING));
      await emailPurchaseOrder({ purchaseOrderUid, data })();
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setEmailPurchaseOrderStatus(STATUS.FULFILLED));
    }
  };

// Pagination
export const setCurrentPage = (search) => (dispatch) => {
  dispatch({
    type: SET_CURRENT_PAGE,
    payload: search,
  });
};

export const setTotalCount = (search) => (dispatch) => {
  dispatch({
    type: SET_TOTAL_COUNT,
    payload: search,
  });
};

export const setNextPageOffset = (search) => (dispatch) => {
  dispatch({
    type: SET_NEXT_PAGE_OFFSET,
    payload: search,
  });
};

export const setPreviousPageOffset = (search) => (dispatch) => {
  dispatch({
    type: SET_PREVIOUS_PAGE_OFFSET,
    payload: search,
  });
};

export const setPageOffset = (search) => (dispatch) => {
  dispatch({
    type: SET_PAGE_OFFSET,
    payload: search,
  });
};

export const setPageLimit = (search) => (dispatch) => {
  dispatch({
    type: SET_PAGE_LIMIT,
    payload: search,
  });
};

export const setSort = (sort) => (dispatch) => {
  dispatch({
    type: SET_SORT,
    payload: sort,
  });
};

export const resetPaginationAction = () => (dispatch) => {
  dispatch(setTotalCount(0));
  dispatch(setCurrentPage(0));
  dispatch(setPageOffset(0));
  dispatch(setNextPageOffset(0));
  dispatch(setPreviousPageOffset(0));
};

export const setIsDuplicating = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_DUPLICATING,
    payload: status,
  });
};

export const setDuplicateOrderUid = (uid) => (dispatch) => {
  dispatch({
    type: SET_DUPLICATE_ORDER_UID,
    payload: uid,
  });
};

export const setDuplicateOrderSupplierUid = (supplierUid) => (dispatch) => {
  dispatch({
    type: SET_DUPLICATE_ORDER_SUPPLIER_UID,
    payload: supplierUid,
  });
};

export const setIsDuplicateConfirmationAlertOpen = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_DUPLICATE_CONFIRMATION_ALERT_OPEN,
    payload: status,
  });
};

export const duplicateOrderAction = (purchaseOrderUid, supplierUid) => async (dispatch) => {
  try {
    dispatch(setIsDuplicating(true));
    const response = await postDuplicateOrder(purchaseOrderUid)();
    const duplicatedOrderId = response.data;
    dispatch(invalidateSearchCacheIfGoSearch("purchase_orders"));

    dispatch(
      alertActions.createAlertAction(
        nanoid(),
        "Order duplicated successfully",
        false, // Disable auto close
        "success",
        "Open new order",
        () => {
          // Navigate to the new duplicated purchase order
          window.location.href = `/suppliers/${supplierUid}/purchase-order/${duplicatedOrderId}`;
        },
        "btn-open"
      )
    );
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsDuplicating(false));
    dispatch(setDuplicateOrderUid(null));
    dispatch(setDuplicateOrderSupplierUid(null));
    dispatch(setIsDuplicateConfirmationAlertOpen(false));
  }
};
export const setIsChangingSupplier = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_CHANGING_SUPPLIER,
    payload: status,
  });
};

export const setIsChangingSupplierModalOpen = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_CHANGING_SUPPLIER_MODAL_OPEN,
    payload: status,
  });
};

export const changeSupplierAction =
  ({ purchaseOrderUid, newSupplierUid, purchaseOrderNumber }) =>
  async (dispatch) => {
    try {
      dispatch(setIsChangingSupplier(STATUS.PENDING));
      await postChangeSupplier({ purchaseOrderUid, newSupplierUid })();
      dispatch(
        alertActions.createAlertAction(
          nanoid(),
          `Supplier changed for Purchase Order #${purchaseOrderNumber}`,
          true,
          "success"
        )
      );
      dispatch(draftPurchaseOrderAction(purchaseOrderUid));
    } catch (error) {
      const parsed = parseApiError(error);
      dispatch(
        alertActions.createAlertAction(
          nanoid(),
          parsed.message,
          true,
          "danger",
          undefined,
          undefined,
          undefined,
          parsed.warnings
        )
      );
    } finally {
      dispatch(setIsChangingSupplier(STATUS.FULFILLED));
      dispatch(setIsChangingSupplierModalOpen(false));
    }
  };

export const setIsConvertingBillOrPO = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_CONVERTING_BILL_OR_PO,
    payload: status,
  });
};

export const setIsConvertingBillOrPOConfirmationAlertOpen = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_CONVERTING_BILL_OR_PO_CONFIRMATION_ALERT_OPEN,
    payload: status,
  });
};

export const editDrawingSVGs = (data) => (dispatch) => {
  dispatch({
    type: EDIT_DRAWING_SVGS,
    payload: data,
  });
};

export const saveProductToCatalogueAction = (payload) => async (dispatch) => {
  try {
    dispatch(
      editActivePurchaseOrderAction(["items", payload.rowIndex, "isSavingToProductCatalogue"], true)
    );
    const { data: priceLevels } = await priceLevelsApi.getPriceLevels()();
    const { data: categories } = await productsApi.getProductCategoriesV2()();

    const miscellaneousCategory = categories.find((c) => c.name === "Miscellaneous");

    const data = {
      name: payload.displayName,
      category_uid: miscellaneousCategory?.uid ?? null,
      tables: [
        {
          material_uid: "",
          colours: [],
          fields: [
            {
              name: "Description",
              is_hide_order_summary: false,
              is_hide_invoice_pdf: false,
              is_hide_customer_view: false,
              is_hide_work_order_pdf: false,
            },
          ],
          values: [
            {
              inventory: [],
              types: [
                {
                  name: "Description",
                  value: payload.productDescription,
                  is_hide_order_summary: false,
                  is_hide_invoice_pdf: false,
                  is_hide_customer_view: false,
                  is_hide_work_order_pdf: false,
                },
              ],
              prices: [
                {
                  name: "Price (A)",
                  value: "",
                },
              ],
              accounting_item: {},
              suppliers: [
                {
                  supplier_uid: payload.supplierUid,
                  price: payload.productRowPrice,
                },
              ],
              is_deleted: false,
              row_prices: priceLevels.map((p) => ({
                price_level_uid: p.uid,
                value: payload.productRowPrice,
              })),
            },
          ],
          price_levels: priceLevels,
          hiddenPriceLevels: [],
        },
      ],
      pricing_strategy: payload.pricingStrategy, // Temporary change to actual value once pricing strategy is merge
      is_deleted: false,
      is_purchased_by_company: true,
      is_sold_by_company: false,
      is_stock_tracked_by_company: false,
      is_margin_tracked_by_company: false,
      suppliers: [
        {
          supplier_uid: payload.supplierUid,
          is_preferred_supplier: false,
        },
      ],
      custom_formula: null,
    };

    const bodyFormData = new FormData();
    bodyFormData.append("json", JSON.stringify(snakecaseKeys(data)));
    bodyFormData.append("image", "");

    const { data: postResponse } = await productsApi.saveProduct(bodyFormData)();

    dispatch(
      batchEditActivePurchaseOrderAction([
        { path: ["items", payload.rowIndex, "isSavingToProductCatalogue"], value: false },
        {
          path: ["items", payload.rowIndex, "productRow"],
          value: postResponse.tables[0].values[0].suppliers[0].uid,
        },
        {
          path: ["items", payload.rowIndex, "selectedProductRow"],
          value: postResponse.tables[0].values[0].uid,
        },
        {
          path: ["items", payload.rowIndex, "productType"],
          value: PRODUCT_TYPE_ADDITIONAL_PRODUCT,
        },
        {
          path: ["items", payload.rowIndex, "displayTypes"],
          value: postResponse.tables[0].values[0].types,
        },
      ])
    );

    dispatch(updatePurchaseOrderSilently(payload.purchaseOrderUid));

    dispatch(
      alertActions.createAlertAction(
        nanoid(),
        `Product saved to catalogue as ${payload.displayName}`,
        true,
        "success"
      )
    );
  } catch (error) {
    handleError(error, dispatch);
  }
};

export const setIsAddProductModalType = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_ADD_PRODUCT_MODAL_TYPE,
    payload: status,
  });
};

export const exportPurchaseOrderToFile =
  ({ search = "", startDate = "", endDate = "", statuses = [] } = {}) =>
  async (dispatch) => {
    try {
      const params = {
        search: search || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        statuses,
      };

      dispatch(setExportPurchaseOrdersStatus(STATUS.PENDING));
      const response = await dispatchApi(
        dispatch,
        EXPORT_PURCHASE_ORDERS_STATUS,
        () =>
          getExportPurchaseOrders({
            params,
          }),
        INTENT.PERSISTENT
      );

      if (response) {
        return resolveFileUrl(response);
      }
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setExportPurchaseOrdersStatus(STATUS.FULFILLED));
    }
  };
